import constants from '../../../../../../appConstants';
import {
  SpecialNeedsRenderer,
  getSpecialNeeds
} from '@dataGrid/components/cellRenderer/index';
import iconRenderer from '../../../../../common/iconRenderer';
import {
  formatDateTimeWithoutUTCConversion,
  mergeUserConfig,
  formatMonthDateYearWithoutUTC
} from '@dataGrid/gridUtilities';
import {
  MultiSelectFilter,
  DateTimeFilter,
  StaticFilter
} from '@dataGrid/components/columnFilters';
import PickupTypesRenderer from '../Grid/cellRenderer/PickupTypesRenderer';
import PickupNumberRenderer from '../Grid/cellRenderer/PickupNumberCellRenderer';
import { getZoneValue } from '../../utils/index';
import PickupIndicatorIconRenderer from '../Grid/cellRenderer/PickupIndicatorIconRenderer';
import { updateGridColumnHeaderNames } from '../../../utils/utils';
import { gridShortHeaderNamesConfig } from '../Grid/constants';
import PriorityRenderer from '../Grid/cellRenderer/PriorityRenderer';
import { getClosingSoonPickupTypes } from '../../../../utils';
import ClosingSoonTimeRenderer from '../Grid/cellRenderer/ClosingSoonTimeRenderer';
import RecommendationHeaderRenderer from '../Grid/headerRenderer/RecommendationHeaderRenderer';
import {
  RECOMMENDATION_HEADER_NAME,
  DRIVER_RECOMMENDATION_COL_ID
} from '../Grid/headerRenderer/constants';
import { pickupTypeValueGetter } from '../PickupManagement/utils';
import GroupCellRenderer from './GroupCellRenderer';
import { isGroupHeaders, isPickupGroupHeader } from './utils';
import {
  isPackagingFieldVisible,
  PACKAGING_FIELDS
} from '../../DriverPopUp/common/packagingConfig';

// Reusable cell renderer functions to avoid creating new functions on each render
const emptyValueRenderer = (params) => params.value || '';
const geoCodingCellRenderer = (params) => {
  if (isPickupGroupHeader(params.data)) {
    return <GroupCellRenderer {...params} />;
  }
  return null;
};
const priorityCellRenderer = (params) => <PriorityRenderer {...params} variant="tooltip" />;

// Cached static functions and objects
const identityTooltipValueGetter = (params) => params.value;

// Cache header component params
const RECOMMENDATION_HEADER_PARAMS = {
  headerWidth: constants.columnWidth.W_300,
  headerName: RECOMMENDATION_HEADER_NAME,
  headerTooltip: RECOMMENDATION_HEADER_NAME
};

// Optimized cellRenderer factory for group rows - creates stable function references
const createGroupCellRenderer = (DefaultRenderer) => {
  // Return a stable function reference that checks group headers first (early return optimization)
  return (params) => {
    if (isGroupHeaders(params.data)) {
      return <GroupCellRenderer {...params} />;
    }
    return (
      <DefaultRenderer
        {...params}
        data={{
            ...params.data,
          }}
      />
    );
  };
};

export default class GridConfig {
  constructor(configuration) {
    const config = configuration || {};
    this.name = 'GridConfiguration';
    this.config = GridConfig.getConfiguration(config);
  }
  static getConfiguration(configuration) {
    const defaultConfig = GridConfig.getDefaultConfiguration(configuration);
    const mergedConfig = mergeUserConfig({
      defaultConfig,
      userConfig: configuration.userConfig
    });
    return updateGridColumnHeaderNames({
      gridShortHeaderNamesConfig,
      columnConfig: mergedConfig,
      enableColumnShortForm: configuration.featureFlags?.enableColumnShortForm
    });
  }

  static getSpecialNeedsOption(params) {
    return params.context.specialNeedsOptions;
  }

  static getPickupTypeOptions(params) {
    return params.context.pickupTypeOptions;
  }

  // Optimized: direct property access with fallback
  static getNonZeroValue = (params) => {
    return params.data?.[params.column?.colId] || '';
  };

  static getRecommendedDriverName = (params) => {
    const recommendedDriver = params.api?.runCommand?.('recommendedDriverByGroupId', {
      groupId: params.data?.groupId
    });
    return recommendedDriver?.driverId
      ? `${recommendedDriver?.driverName ?? ''} (${recommendedDriver?.driverNumber})`
      : '';
  };

  static getPackageTypes = (params) => {
    return params.data?.packageType?.join(',');
  };

  static renderRecommendedDriver = (params) => {
    if (isPickupGroupHeader(params.data)) {
      return <GroupCellRenderer {...params} />;
    }
    return null;
  };

  // Optimized: create columns directly without caching, using object spread for efficiency
  static getRouteNameColumns(showStaticRoute) {
    // Base driver route column configuration (reused to avoid duplication)
    const baseDriverRouteColumn = {
      headerName: 'Driver Route',
      headerTooltip: 'Driver Route Name',
      field: 'routeName',
      minWidth: constants.columnWidth.DRIVER_ROUTE,
      cellClass: 'gridCell-leftAlign',
      filter: MultiSelectFilter
    };

    // Early return optimization: check condition once
    if (showStaticRoute) {
      return [
        {
          headerName: 'Route',
          headerTooltip: 'Route Name',
          field: 'staticRoute',
          minWidth: constants.columnWidth.W_90,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          ...baseDriverRouteColumn,
          hide: true
        }
      ];
    }

    return [
      {
        ...baseDriverRouteColumn,
        hide: false
      }
    ];
  }

  static getClosingSoonValue = (params) => {
    const closingTimeDiff = params.context?.getClosingSoonPickupTime?.(params.data?.pickupId);
    return closingTimeDiff ?? null;
  };

  static isClosingSoonPickupFiltered = ({ value, selectedOptions, context }) => {
    const { closingSoonLimitConfig } = context;
    const selectedSet = new Set(selectedOptions); // Convert to Set for O(1) lookups
    
    // Fast path: check common cases first
    if (value === null) {
      return selectedSet.has('');
    }
    
    // Use direct checks with Set for better performance
    if (value > 0) {
      if (value <= closingSoonLimitConfig.closingSoonAlertTimeLimit) {
        // Alert also matches warning, so check both
        return selectedSet.has(constants.CLOSING_SOON_PICKUP_TYPES.ALERT) ||
               selectedSet.has(constants.CLOSING_SOON_PICKUP_TYPES.WARNING);
      }
      if (value <= closingSoonLimitConfig.closingSoonWarningTimeLimit) {
        return selectedSet.has(constants.CLOSING_SOON_PICKUP_TYPES.WARNING);
      }
      return false;
    }
    
    // value <= 0 (closed)
    return selectedSet.has(constants.CLOSING_SOON_PICKUP_TYPES.CLOSED);
  };

  static geoCodeValueGetter(params) {
    return params.data?.isGeoCoded;
  }

  static getDefaultConfiguration(configuration) {
    const routeNameColumns = GridConfig.getRouteNameColumns(
      configuration.showStaticRoute
    );
    const loosePiecesVisible = isPackagingFieldVisible(
      configuration.pickupPackagingDetailsConfig,
      PACKAGING_FIELDS.LOOSE_PIECES
    );
    return {
      title: constants.gridNames.PickupsGrid,
      // keyColumn: "pickupId",
      rowSelection: 'multiple',
      rowDeselection: true,
      defaultColDef: {},
      columns: [
        {
          headerName: '',
          minWidth: constants.columnWidth.MERGED_PICKUP_ICON,
          maxWidth: constants.columnWidth.MERGED_PICKUP_ICON,
          pinned: 'left',
          field: 'isMergedPickup',
          lockPosition: true,
          cellClass: ['gridCell-centerAlign', 'gridCell-merge'],
          filter: false,
          cellRenderer: createGroupCellRenderer(PickupIndicatorIconRenderer),
          isInvisibleInShowHide: true
        },
        {
          headerName: 'Shipper Name',
          headerTooltip: 'Shipper Name',
          field: 'shipperName',
          pinned: 'left',
          lockPosition: true,
          lockPinned: true,
          minWidth: constants.columnWidth.CONSIGNEE_NAME,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'shipperName',
          filterParams: {
            width: constants.columnWidth.W_300
          },
          cellRenderer: createGroupCellRenderer(emptyValueRenderer)
        },
        {
          headerName: 'Pickup Number',
          headerTooltip: 'Pickup Number',
          field: 'pickupNumberList',
          valueGetter: (params) => {
            const data = params.data;
            return isGroupHeaders(data) ? '' : (data?.pickupNumber ?? '');
          },
          minWidth: constants.columnWidth.W_240,
          cellClass: 'gridCell-leftAlign',
          pinned: 'left',
          lockPosition: true,
          filter: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING,
            isValueList: true
          },
          cellRenderer: createGroupCellRenderer(PickupNumberRenderer)
        },
        {
          headerName: 'Closing Soon',
          headerTooltip: 'Closing Soon',
          valueGetter: GridConfig.getClosingSoonValue,
          minWidth: constants.columnWidth.W_180,
          cellClass: 'gridCell-leftAlign',
          filter: StaticFilter,
          colId: 'closingSoon',
          tooltipValueGetter: identityTooltipValueGetter,
          filterParams: {
            getStaticOptions: getClosingSoonPickupTypes,
            filterFunction: GridConfig.isClosingSoonPickupFiltered
          },
          cellRenderer: ClosingSoonTimeRenderer
        },
        {
          colId: DRIVER_RECOMMENDATION_COL_ID,
          headerName: RECOMMENDATION_HEADER_NAME,
          valueGetter: GridConfig.getRecommendedDriverName,
          minWidth: constants.columnWidth.W_300,
          cellClass: 'gridCell-leftAlign',
          pinned: 'left',
          filter: MultiSelectFilter,
          cellRenderer: GridConfig.renderRecommendedDriver,
          headerComponent: RecommendationHeaderRenderer,
          headerComponentParams: RECOMMENDATION_HEADER_PARAMS,
          hide: !configuration.featureFlags?.isRecommendationsEnabled,
          isInvisibleInShowHide: !configuration.featureFlags?.isRecommendationsEnabled
        },
        {
          headerName: 'Zone',
          headerTooltip: 'Zone',
          valueGetter: getZoneValue,
          minWidth: constants.columnWidth.DISPATCH_ZONE,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          filterParams: {
            addDefaultBlank: true
          }
        },
        {
          headerName: 'Driver',
          headerTooltip: 'Driver Name',
          field: 'driverName',
          minWidth: constants.columnWidth.DISPATCH_DRIVER_NAME,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        ...routeNameColumns,
        {
          headerName: 'Type',
          headerTooltip: 'Type',
          colId:'type',
          valueGetter: pickupTypeValueGetter,
          minWidth: constants.columnWidth.PICKUP_TYPE,
          cellClass: 'gridCell-leftAlign',
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getPickupTypeOptions
          },
          cellRenderer: PickupTypesRenderer
        },

        {
          headerName: 'Address',
          field: 'shipperAddress',
          minWidth: constants.columnWidth.CUSTOMER_ADDRESS,
          headerTooltip: 'Shipper Address',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'shipperAddress',
          filterParams: {
            width: constants.columnWidth.W_300
          }
        },
        {
          headerName: 'City',
          field: 'shipperCity',
          headerTooltip: 'Shipper City',
          minWidth: constants.columnWidth.CITY,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'State',
          field: 'shipperState',
          headerTooltip: 'Shipper State',
          minWidth: constants.columnWidth.STATE,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Zip',
          field: 'shipperZip',
          headerTooltip: 'Shipper Zip',
          minWidth: constants.columnWidth.ZIP_CODE,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Pickup Date',
          field: 'pickupDate',
          headerTooltip: 'Pickup Date',
          minWidth: constants.columnWidth.PICKUP_DATE,
          cellClass: 'gridCell-leftAlign',
          valueFormatter: (params) => formatMonthDateYearWithoutUTC(params),
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.MONTHDATEYEAR,
          },
        },
        {
          headerName: 'Ready Time',
          field: 'pickupReadyTime',
          headerTooltip: 'Ready Time',
          minWidth: constants.columnWidth.W_120,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME,
            isTimeInterval: true
          }
        },
        {
          headerName: 'Close Time',
          field: 'pickupCloseTime',
          headerTooltip: 'Close Time',
          minWidth: constants.columnWidth.W_115,
          cellClass: 'gridCell-leftAlign',
          filter: DateTimeFilter,
          filterParams: {
            DATETYPE: constants.DATETYPES.TIME
          }
        },
        {
          headerName: 'Priority',
          minWidth: constants.columnWidth.PRIORITY,
          field: 'pickupPriority',
          headerTooltip: 'Priority',
          cellClass: 'gridCell-leftAlign',
          cellRenderer: priorityCellRenderer,
          filter: MultiSelectFilter,
          filterParams: {
            cellRenderer: PriorityRenderer
          }
        },
        {
          headerName: 'Spl Needs',
          colId: 'specialNeeds',
          minWidth: constants.columnWidth.SPL_NEED,
          valueGetter: getSpecialNeeds,
          headerTooltip: 'Special Needs',
          cellClass: 'gridCell-leftAlign',
          cellRenderer: SpecialNeedsRenderer,
          filter: StaticFilter,
          filterParams: {
            getStaticOptions: GridConfig.getSpecialNeedsOption
          }
        },
        {
          headerName: 'Bills',
          field: 'bills',
          minWidth: constants.columnWidth.BILLS,
          headerTooltip: 'Bills',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Quote #',
          field: 'quoteNumber',
          minWidth: constants.columnWidth.W_125,
          headerTooltip: 'Quote Number',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          cellRenderer: createGroupCellRenderer(emptyValueRenderer)
        },
        {
          headerName: 'Pieces',
          field: 'pieces',
          minWidth: constants.columnWidth.W_85,
          headerTooltip: 'Pieces',
          cellClass: 'gridCell-leftAlign',
          valueGetter: GridConfig.getNonZeroValue,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Loose Pieces',
          field: 'loosePieces',
          minWidth: constants.columnWidth.W_100,
          headerTooltip: 'Loose Pieces',
          cellClass: 'gridCell-leftAlign',
          valueGetter: GridConfig.getNonZeroValue,
          filter: MultiSelectFilter,
          hide: !loosePiecesVisible,
          isInvisibleInShowHide: !loosePiecesVisible
        },
        {
          headerName: 'HU',
          field: 'hu',
          minWidth: constants.columnWidth.W_80,
          headerTooltip: 'Handling Unit',
          cellClass: 'gridCell-leftAlign',
          valueGetter: GridConfig.getNonZeroValue,
          filter: MultiSelectFilter
        },
        {
          headerName: 'Geocoding',
          headerTooltip: 'Geocoding',
          colId: 'isGeoCoded',
          valueGetter: GridConfig.geoCodeValueGetter,
          minWidth: constants.columnWidth.GEOCODING,
          cellClass: 'gridCell-leftAlign',
          editable: false,
          cellRenderer: geoCodingCellRenderer,
          filter: StaticFilter,
          filterParams: {
            staticOptions: Object.values(constants.geoCode),
            renderIcon: iconRenderer
          }
        },
        {
          headerName: 'Pkg Type',
          valueGetter: GridConfig.getPackageTypes,
          minWidth: constants.columnWidth.PKG_TYPE,
          headerTooltip: 'Package Type',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          cellRenderer: createGroupCellRenderer(emptyValueRenderer)
        },
        {
          headerName: 'Weight',
          field: 'weight',
          minWidth: constants.columnWidth.W_90,
          headerTooltip: 'Weight',
          cellClass: 'gridCell-centerAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Special Instruction',
          field: 'specialInstructions',
          minWidth: constants.columnWidth.SPECIAL_INSTRUCTION,
          headerTooltip: 'Special Instructions',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          tooltipField: 'specialInstructions',
          sortable: false,
          cellRenderer: createGroupCellRenderer(emptyValueRenderer)
        },
        {
          headerName: 'Driver Number',
          headerTooltip: 'Driver Number',
          field: 'driverNumber',
          minWidth: constants.columnWidth.DRIVER_NUMBER,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          filterParams: {
            filterType: constants.FILTER_TYPES.STRING
          }
        },
        {
          headerName: 'Prev Assigned Driver',
          headerTooltip: 'Previously Assigned Driver',
          field: 'prevAssignedDriver',
          minWidth: constants.columnWidth.W_180,
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter
        },
        {
          headerName: 'Priority Code',
          minWidth: constants.columnWidth.W_100,
          field: 'pickupPriority',
          headerTooltip: 'Priority Code',
          cellClass: 'gridCell-leftAlign',
          filter: MultiSelectFilter,
          hide: true,
        },
        {
          headerName: "Created DateTime",
          headerTooltip: "Created Date Time",
          field: "createdDateTime",
          minWidth: constants.columnWidth.W_180,
          valueFormatter: formatDateTimeWithoutUTCConversion,
          filter: DateTimeFilter,
          tooltipField: "createdDateTime",
          filterParams: {
            DATETYPE: constants.DATETYPES.DATETIME,
          }
        },
      ]
    };
  }
}
